import React, { useContext, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from './mui/theme';
import { Modules, RouteParamsTypes, AuthRoutes, NonAuthRoutes } from './enums';
import { AuthContext } from './contexts/AuthContext';
import { TenantContext } from './contexts/TenantContext';
import { redirectBasedOnPermission } from './utils/methods';

import Auth from './routes/Auth';
import Resources from './routes/Resources';
import My from './routes/My';
import Home from './routes/Home';
// import Tasks from './routes/Tasks';
import Templates from './routes/Templates';
import Products from './routes/Products';
import MonthlyStock from './routes/MonthlyStock';
import Errors from './routes/Errors';

import HttpInterceptor from './components/HttpInterceptor';
import UnHandledRejection from './components/ErrorPages/UnHandledRejection';
import GlobalLoader from './components/GlobalLoader';

import './assets/vendor/fonts/remixicon/remixicon.css';
import './assets/vendor/fonts/flag-icons.css';
import './assets/vendor/libs/node-waves/node-waves.css';
import './assets/css/demo.css';
import './assets/vendor/libs/perfect-scrollbar/perfect-scrollbar.css';
import './assets/vendor/libs/typeahead-js/typeahead.css';
import './assets/vendor/libs/datatables-bs5/datatables.bootstrap5.css';
import './assets/vendor/libs/datatables-responsive-bs5/responsive.bootstrap5.css';
import './assets/vendor/libs/datatables-buttons-bs5/buttons.bootstrap5.css';
import './assets/vendor/libs/datatables-checkboxes-jquery/datatables.checkboxes.css';
import './assets/vendor/libs/select2/select2.css';
import './assets/vendor/libs/@form-validation/form-validation.css';
import './assets/vendor/css/pages/page-auth.css';
import './assets/vendor/css/pages/page-profile.css';
import './assets/app-only/css/style.scss';
import 'react-toastify/dist/ReactToastify.css';

import './i18n/config';

const App: React.FC = () => {
  const { token, theme, permissions, isSystemAdmin, exception, loading, setLoading } =
    useContext(AuthContext);
  const { isValidating: isTenantValidating } = useContext(TenantContext);
  const themeLinkRef = useRef<HTMLLinkElement | null>(null);
  const themeLinkRefTwo = useRef<HTMLLinkElement | null>(null);

  const loadCSS = (href: string): HTMLLinkElement => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.type = 'text/css';
    document.head.appendChild(link);
    return link;
  };

  useEffect(() => {
    const root = document.documentElement;
    if (themeLinkRef.current) {
      document.head.removeChild(themeLinkRef.current);
    }

    if (themeLinkRefTwo.current) {
      document.head.removeChild(themeLinkRefTwo.current);
    }

    const href = theme === 'light' ? '/theme/light/core.css' : '/theme/dark/core.css';
    const hrefTwo =
      theme === 'light' ? '/theme/light/theme-semi-dark.css' : '/theme/dark/theme-semi-dark.css';
    const hrefThree = '/theme/responsive.css';
    themeLinkRef.current = loadCSS(href);
    themeLinkRefTwo.current = loadCSS(hrefTwo);
    themeLinkRefTwo.current = loadCSS(hrefThree);
    root.classList.add(theme === 'light' ? 'light-style' : 'dark-style');
    root.classList.remove(theme === 'light' ? 'dark-style' : 'light-style');

    setTimeout(() => {
      setLoading('false');
    }, 100);
  }, [theme]);

  const handleBlankRoute = useCallback(() => {
    if (!token) {
      return Modules.auth;
    } else {
      return redirectBasedOnPermission(isSystemAdmin, permissions);
    }
  }, [token, isSystemAdmin, permissions]);

  const handleAnyRoute = useCallback(() => {
    if (!token) {
      return Modules.auth;
    }
    return Modules.error + NonAuthRoutes.notFound;
  }, [token]);

  const isLoading = loading === 'true' || isTenantValidating;

  return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <Router>
        {exception && <UnHandledRejection />}
        {isLoading && <GlobalLoader />}

        <HttpInterceptor />
        <ToastContainer
          position="top-center"
          autoClose={5000}
          transition={Slide}
          newestOnTop
          closeOnClick
          rtl={false}
          draggable={false}
          closeButton={false}
          className="toast-container-custom"
        />

        {!isLoading && (
          <Routes>
            <Route
              path={AuthRoutes.blank}
              element={<Navigate replace to={{ pathname: handleBlankRoute() }} />}
            />
            <Route path={Modules.auth + RouteParamsTypes.any} element={<Auth />} />
            <Route path={Modules.resources + RouteParamsTypes.any} element={<Resources />} />
            <Route path={Modules.my + RouteParamsTypes.any} element={<My />} />
            <Route path={Modules.home + RouteParamsTypes.any} element={<Home />} />
            {/* <Route path={Modules.tasks + RouteParamsTypes.any} element={<Tasks />} /> */}
            <Route path={Modules.templates + RouteParamsTypes.any} element={<Templates />} />
            <Route path={Modules.products + RouteParamsTypes.any} element={<Products />} />
            <Route path={Modules.monthlyStock + RouteParamsTypes.any} element={<MonthlyStock />} />
            <Route path={Modules.error + RouteParamsTypes.any} element={<Errors />} />
            <Route
              path={RouteParamsTypes.any}
              element={<Navigate replace to={{ pathname: handleAnyRoute() }} />}
            />
          </Routes>
        )}
      </Router>
    </ThemeProvider>
  );
};

export default App;
